import React, { useState } from 'react';
import { Container } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_CERTIFICATE,
  GET_ALL_CERTIFICATES,
  UPDATE_CERTIFICATE,
  REMOVE_CERTIFICATE,
  DUPLICATE_CERTIFICATE,
  GET_CERTIFICATE_DETAILS,
  GET_CERTIFICATE_TYPE_ENUM,
} from '../../../graph-queries/tar/certificate';
import { TCertificate } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';
import {
  useCurrentUsername,
  useHasMaintenanceAdminAccess,
  useHasMaintenanceUserAccess,
} from '../../../lib/util/auth';
import AspectsTable from '../../organisms/AspectsTable/AspectsTable';
import CertificateForm from '../../organisms/CertificateForm/CertificateForm';
import { ColumnDef } from '@tanstack/react-table';
import { extractAndFormatName, useAlertText } from '../../organisms/AspectsTable/util';
import { useLocation, useNavigate } from 'react-router-dom';
import { MAINTANENCE_PATH } from '../../../constants';
import { CertificateStatusEnum, DatabaseEnum } from '../../../graph-queries/__generated__/graphql';
import CertificateFiltersModal from '../../organisms/CertificateFilter/CertificateFiltersModal';
import { INITIAL_CERTIFICATE_FILTERS } from '../../../lib/constants';
import { useBrowserLocationState } from '../../../lib/util/browser';
import { CERTIFICATE_STATUS_ENUM } from '../../../lib/constants/dbConstants';
import { certificatesGlobalFilterFn } from '../../organisms/CertificateFilter/CertificateFilter.util';
import DuplicateCertificateForm from './DuplicateCertificateForm';

export const columns: ColumnDef<TCertificate>[] = [
  {
    header: 'Certificate Title',
    accessorKey: 'title',
    cell: (props) => {
      const location = useLocation();
      const navigate = useNavigate();
      const certificateIds = props.table
        .getSortedRowModel()
        .rows.map((r) => r.original.certificateId);
      const row = props.row.original;

      return (
        <a
          title={`MO: ${row.maintenanceOrder}${row.functionalLocation ? `, FLOC: ${row.functionalLocation}` : ''}`}
          href={
            row.status === 'COMPLETE' || row.status === 'APPROVED'
              ? `/${MAINTANENCE_PATH}/certificates/execute/${row.certificateId}${location.search}`
              : `/${MAINTANENCE_PATH}/certificates/${row.certificateId}${location.search}`
          }
          onClick={(e) => {
            e.preventDefault();
            row.status === 'COMPLETE' || (row.status === 'APPROVED' && row.template === false)
              ? navigate(
                  `/${MAINTANENCE_PATH}/certificates/execute/${row.certificateId}${location.search}`,
                  {
                    state: { certificateIds },
                  }
                )
              : navigate(
                  `/${MAINTANENCE_PATH}/certificates/${row.certificateId}${location.search}`,
                  {
                    state: { certificateIds },
                  }
                );
          }}
          style={{ textDecoration: 'underline', color: 'blue', cursor: 'pointer' }}
        >
          {row.title}
        </a>
      );
    },
  },
  {
    header: 'Certificate Number',
    accessorKey: 'certificateNumber',
    accessorFn: (row) => row.certificateNumber || row.certificateId,
  },
  {
    header: 'Status',
    accessorKey: 'status',
    accessorFn: (row) => CERTIFICATE_STATUS_ENUM[row.status as CertificateStatusEnum],
  },
  {
    header: 'MC1',
    accessorKey: 'maintenanceMC1.mc1Number',
  },
  {
    header: 'SH1',
    accessorKey: 'maintenanceSH1.sh1Number',
  },
  {
    header: 'System',
    accessorKey: 'system',
  },
  {
    header: 'CertificateId',
    accessorKey: 'certificateId',
    enableHiding: true,
  },
  {
    header: 'Created By',
    accessorKey: 'createdBy',
    accessorFn: (row) => extractAndFormatName(row.createdBy),
  },
  {
    header: 'Checked By',
    accessorKey: 'checkedBy',
    accessorFn: (row) => extractAndFormatName(row.checkedBy),
  },
];

const CertificatesPage: React.FC = () => {
  const { alertText, setAlertText } = useAlertText();

  const currentUsername = useCurrentUsername();
  const hasMaintenanceUserAccess = useHasMaintenanceUserAccess();
  const hasMaintenanceAdminAccess = useHasMaintenanceAdminAccess();
  const asset = useSelectedAsset();
  const [filters, setFilters] = useBrowserLocationState('filters', INITIAL_CERTIFICATE_FILTERS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { data, loading } = useQuery(GET_ALL_CERTIFICATES, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });
  const { data: types } = useQuery(GET_CERTIFICATE_TYPE_ENUM, {});
  const certificateTypes = (types?.certificateTypeEnum as unknown as DatabaseEnum[]) || [];
  const [createCertificate, { loading: isCreating }] = useMutation(CREATE_CERTIFICATE, {
    refetchQueries: [GET_ALL_CERTIFICATES],
    awaitRefetchQueries: true,
  });
  const [updateCertificate, { loading: isUpdating }] = useMutation(UPDATE_CERTIFICATE, {
    refetchQueries: (mutationResult) => {
      const certificateId = mutationResult.data?.updateCertificate?.certificateId;
      return [
        GET_ALL_CERTIFICATES,
        // refetch the certificate details for the updated certificate
        ...(certificateId
          ? [{ query: GET_CERTIFICATE_DETAILS, variables: { certificateId } }]
          : []),
      ];
    },
    awaitRefetchQueries: true,
  });
  const [removeCertificate, { loading: isRemoving }] = useMutation(REMOVE_CERTIFICATE, {
    refetchQueries: [GET_ALL_CERTIFICATES],
    awaitRefetchQueries: true,
  });
  const [duplicateCertificate, { loading: isDuplicating }] = useMutation(DUPLICATE_CERTIFICATE, {
    refetchQueries: [GET_ALL_CERTIFICATES],
    awaitRefetchQueries: true,
  });

  const location = useLocation();
  const navigate = useNavigate();

  const onAddSubmit = (certificate: TCertificate) => {
    const { createdAt, updatedAt, status, ...input } = certificate;
    return createCertificate({ variables: { input } }).then((res) => {
      navigate(
        `/${MAINTANENCE_PATH}/Certificates/${res.data?.createCertificate.certificateId}${location.search}`
      );
      return res;
    });
  };

  const onEditSubmit = (certificate: TCertificate) => {
    const { createdAt, updatedAt, status, ...input } = certificate;
    return updateCertificate({ variables: { input } }).then(() =>
      setAlertText('Certificate successfully updated')
    );
  };

  const onRemoveSubmit = (selection: TCertificate[]) => {
    return Promise.all(
      selection.map((certificate) =>
        removeCertificate({
          variables: { certificateId: certificate.certificateId },
        })
      )
    );
  };

  const onDuplicationSubmit = async (certificateId: TCertificate['certificateId']) => {
    return duplicateCertificate({ variables: { certificateId } }).then((res) => {
      navigate(
        `/${MAINTANENCE_PATH}/Certificates/${res.data?.duplicateCertificate.certificateId}${location.search}`
      );
      return res;
    });
  };

  const isLoading =
    isCreating || isUpdating || isRemoving || isDuplicating || loading || !!alertText;

  return (
    <Layout>
      <Container>
        {isFilterOpen && (
          <CertificateFiltersModal
            defaultValues={filters}
            onSubmit={setFilters}
            onReset={() => {
              setFilters(INITIAL_CERTIFICATE_FILTERS);
              setIsFilterOpen(false);
            }}
            onClose={() => setIsFilterOpen(false)}
          />
        )}
        {isLoading ? (
          'Loading...'
        ) : (
          <AspectsTable<TCertificate>
            hideSelectionColumn
            title="New Certificates"
            aspectName="certificate"
            data={(data?.allCertificates as unknown as TCertificate[]) || []}
            columns={columns}
            defaultSortingColumn="certificateId"
            defaultSortingDirection={true}
            hiddenColumn="certificateId"
            alertText={alertText}
            renderAddForm={(onClose) => (
              <CertificateForm
                onSubmit={onAddSubmit}
                onClose={onClose}
                type={'add'}
                isLoading={isLoading}
              />
            )}
            renderEditForm={(onClose, defaultValue) => (
              <CertificateForm
                defaultValues={defaultValue}
                onSubmit={onEditSubmit}
                onClose={onClose}
                type={'edit'}
                isLoading={isLoading}
              />
            )}
            renderDuplicationForm={(onClose, defaultValue) => (
              <DuplicateCertificateForm
                certificateId={defaultValue.certificateId}
                onSubmit={onDuplicationSubmit}
                onClose={onClose}
                isLoading={isLoading}
              />
            )}
            onRemoveSubmit={onRemoveSubmit}
            creationDisabled={!hasMaintenanceUserAccess}
            getRowMutationDisabled={(row) => {
              const isApprovedOrComplete =
                row?.status === CertificateStatusEnum.Approved ||
                row?.status === CertificateStatusEnum.Complete;
              const lacksAdminAccessAndIsNotCreator =
                !hasMaintenanceAdminAccess && row?.createdBy !== currentUsername;
              return isApprovedOrComplete && lacksAdminAccessAndIsNotCreator;
            }}
            getRowDeletionDisabled={(row) => {
              const isApprovedOrComplete =
                row?.status === CertificateStatusEnum.Approved ||
                row?.status === CertificateStatusEnum.Complete;
              const lacksAdminAccessAndIsNotCreator =
                !hasMaintenanceAdminAccess && row?.createdBy !== currentUsername;
              return isApprovedOrComplete && lacksAdminAccessAndIsNotCreator;
            }}
            searchEnabled={true}
            searchPlaceholderText="Search for certificates..."
            filters={filters}
            onFilterClick={() => setIsFilterOpen(true)}
            onUpdateFilters={(newFilters) => setFilters(newFilters)}
            globalFilterFn={certificatesGlobalFilterFn}
            isLoading={isLoading}
          />
        )}
      </Container>
    </Layout>
  );
};

export default CertificatesPage;
